import { Close, Edit, Restore } from '@mui/icons-material';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState, FC } from 'react';
import { Pagination, Table } from '../../../components';
import { AdminContext } from '../../../context/admin';
import { getDomainUsers, resetTwoFactorAuth, getAllReportsTo } from '../../../fetch';
import { isAllowedToBeEditedOrDeleted, alphaSort } from '../../../helpers';
import { IDomainUser, IManagePlaceOrderReportsTo, IDropdownResponse } from '../../../models';
import { MobileAdminUser } from './MobileAdminUser';
import { InternalUserModal } from './internal-user-modal';
import { InternalFilters } from './internal-filters';
import { getAdminLookupPlaceOrderRoles } from '../../../fetch/lookups';

interface IInternalUsers {
  displayName?: string;
}

export const InternalUsers: FC<IInternalUsers> = ({ displayName }) => {
  const classes = useStyles();
  const { selectedUsersTab } = useContext(AdminContext);
  const [isLoading, setLoading] = useState(false);
  const [displayNameParam, setDisplayNameParam] = useState<string>(displayName);
  const [internalUsers, setInternalUsers] = useState<IDomainUser[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [reportsToUsers, setReportsToUsers] = useState<IManagePlaceOrderReportsTo[] | null>(null);
  const [isLoadingReportsTo, setLoadingReportsTo] = useState<boolean>(true);
  const [isSelectRoleModalOpen, setIsSelectRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IDomainUser | null>(null);
  const isMobile = useMediaQuery('(max-width: 1100px)');
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();
  const [isResetting, setResetting] = useState<{
    [id: string]: boolean;
  }>({});
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: 'Asc' | 'Desc';
    AccountName?: 'Asc' | 'Desc';
    Role?: 'Asc' | 'Desc';
    ReportsTo?: 'Asc' | 'Desc';
  }>({
    Name: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    role?: string | number;
    reportsTo?: string;
  } | null>({});
  const [selectedRole, setSelectedRole] = useState<string | number>('');
  const [selectedReportsTo, setSelectedReportsTo] = useState<string>('');
  const [roles, setRoles] = useState<IDropdownResponse[]>([]);
  const [isLoadingRoles, setLoadingRoles] = useState<boolean>(true);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const placeOrderRoles = await getAdminLookupPlaceOrderRoles();
      setRoles(alphaSort(placeOrderRoles, 'description'));
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchReportsToUsers = async () => {
    try {
      setLoadingReportsTo(true);
      const res = await getAllReportsTo();
      setReportsToUsers(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingReportsTo(false);
    }
  };

  const handleResetAuth = async (original: IDomainUser, showLoader?: boolean) => {
    const result = window.confirm('Are you sure you want to reset two factor auth for this user?');
    if (result) {
      if (showLoader) {
        setResetting({
          [original.accountName]: true
        });
      }
      try {
        await resetTwoFactorAuth(original.accountName);
        enqueueSnackbar('Two factor auth reset!', {
          variant: 'success'
        });
      } catch (error: any) {
        const variant =
          error?.Detail &&
          (error.Detail.includes('User not enrolled in MFA') ||
            error.Detail.includes('All user MFA enrollments inactive'))
            ? 'warning'
            : 'error';
        enqueueSnackbar(error?.Detail ?? 'User does not exist', {
          variant: variant
        });
      } finally {
        setResetting({
          [original.accountName]: false
        });
      }
    }
  };

  const fetchInternalUsers = async (clearSearch?: boolean) => {
    setLoading(true);
    try {
      const inUsers = await getDomainUsers({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        query: clearSearch ? undefined : searchValue,
        roleId: filters?.role,
        reportsTo: filters?.reportsTo
      });
      if (!clearSearch && searchValue) {
        setSearchedValue(searchValue);
      } else if (clearSearch) {
        setSearch('');
        setSearchedValue('');
      }
      setRecordCount(inUsers.totalRecordCount);
      setInternalUsers(inUsers.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'displayName',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Email',
        accessor: 'accountName',
        isServerSorted: selectedSort === 'AccountName',
        isServerSortedDesc: sortDirection.AccountName === 'Desc',
        handleClickColumn: () => handleClickColumn('AccountName'),
        Cell: props => (
          <span>
            {props.cell.row.original && props.cell.row.original.accountName
              ? `${props.cell.row.original.accountName}@stonemartin.com`
              : ''}
          </span>
        )
      },
      {
        Header: 'Role',
        accessor: 'role',
        isServerSorted: selectedSort === 'Role',
        isServerSortedDesc: sortDirection.Role === 'Desc',
        handleClickColumn: () => handleClickColumn('Role'),
        Cell: props => (
          // Regex adds a space between the role value
          <span>
            {props.cell.row.original?.placeOrderRole?.name
              ? props.cell.row.original?.placeOrderRole?.name.trim()
              : ''}
          </span>
        )
      },
      {
        Header: 'Reports To',
        accessor: 'reportsTo',
        isServerSorted: selectedSort === 'ReportsTo',
        isServerSortedDesc: sortDirection.ReportsTo === 'Desc',
        handleClickColumn: () => handleClickColumn('ReportsTo'),
        Cell: props => (
          // Regex adds a space between the role value
          <span>
            {props.cell.row.original?.reportsTo?.displayName
              ? !isAllowedToBeEditedOrDeleted(props.cell.row.original?.placeOrderRole?.name)
                ? ''
                : props.cell.row.original?.reportsTo?.displayName
              : ''}
          </span>
        )
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 400,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDomainUser } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                className={classes.editButton}
                onClick={() => {
                  handleEdit(original);
                }}
              >
                Edit
              </Button>
              <LoadingButton
                id="reset-two-factor"
                color="secondary"
                loading={isResetting[original.accountName]}
                loadingPosition="start"
                startIcon={<Restore />}
                onClick={() => handleResetAuth(original, true)}
              >
                Reset Two Factor Auth
              </LoadingButton>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalUsers, isResetting]);

  useEffect(() => {
    fetchRoles();
    fetchReportsToUsers();
  }, []);

  useEffect(() => {
    fetchInternalUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsersTab, page, perPage, selectedSort, sortDirection, filters]);

  const handleEdit = async (domainUser: IDomainUser) => {
    setSelectedUser(domainUser);
    setIsSelectRoleModalOpen(true);
  };

  useEffect(() => {
    const findInternalUser = async () => {
      try {
        if (displayNameParam) {
          const users = await getDomainUsers({
            query: displayNameParam
          });
          const selectedUser = users?.records?.find(
            user => user.displayName === displayNameParam || user.accountName === displayNameParam
          );
          if (selectedUser) {
            handleEdit(selectedUser);
          }
        }
      } catch (error) {}
    };

    findInternalUser();
  }, [displayNameParam, internalUsers]);

  return (
    <>
      <Box mt={1}>
        <InternalFilters
          isLoading={isLoading}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          setSearchValue={setSearch}
          setSearchedValue={setSearchedValue}
          searchValue={searchValue}
          setSelectedRole={setSelectedRole}
          selectedRole={selectedRole}
          setSelectedReportsTo={setSelectedReportsTo}
          selectedReportsTo={selectedReportsTo}
          isLoadingRoles={isLoadingRoles}
          roles={roles}
          reportsTo={reportsToUsers}
          isLoadingAvailableReportsTo={isLoadingReportsTo}
          handleSearch={(val: string) => {
            setPage(0);
            setSearchedValue(val);
            setFilters({
              ...filters,
              search: val
            });
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                search: '',
                role: undefined,
                reportsTo: undefined
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                search: searchValue,
                role: selectedRole || undefined,
                reportsTo: selectedReportsTo || undefined
              });
            }
          }}
        />
      </Box>
      {searchedValue && (
        <Box marginTop="1rem" display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            id="clear-search"
            size="small"
            startIcon={<Close />}
            className={classes.clearSearchButton}
            onClick={() => {
              setSearch('');
              setSearchedValue('');
              fetchInternalUsers(true);
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <Box marginTop="1rem">
        <Table
          columns={columns}
          data={internalUsers}
          isLoading={isLoading}
          hidePagination
          mobileBreakPoint={1100}
          ResponsiveComponent={isMobile ? MobileAdminUser : undefined}
          mobileProps={{
            handleEdit: (val: any) => handleEdit(val),
            handleResetAuth: (val: IDomainUser) => handleResetAuth(val, true)
          }}
        />
        {!isLoading && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Box>
      {selectedUser && (
        <InternalUserModal
          currentUser={selectedUser}
          open={isSelectRoleModalOpen}
          onClose={() => {
            setSelectedUser(null);
            setIsSelectRoleModalOpen(false);
            setDisplayNameParam('');
          }}
          fetchUsers={() => {
            fetchReportsToUsers();
            fetchInternalUsers();
          }}
        />
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  editButton: {
    textAlign: 'right'
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  }
}));
